import React, { useState, useEffect } from 'react';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import Button from '@mui/material/Button';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDPagination from 'components/MDPagination';
import Icon from '@mui/material/Icon';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import Footer from 'examples/Footer';
import DataTable from 'examples/Tables/DataTable';
import { apiGetMachines, apiExcelMachine, apiDeleteMachine } from '../../api/machine.api';
import confirmHardDelete from 'utils/confirmHardDelete';
import MDBadge from 'components/MDBadge';
import { useNavigate, useLocation } from 'react-router-dom';
import { useData } from '../../hooks';
import TopNavbar from 'examples/Navbars/TopNavbar';
import MDButton from '../../components/MDButton';

const Machines = () => {
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, appIsReady } = useData();

  // URL에서 페이지와 검색 조건 정보 추출
  const searchParams = new URLSearchParams(location.search);
  const initialPage = parseInt(searchParams.get('page')) || 1;
  const initialSearchCond = searchParams.get('searchcond') || '';

  const [page, setPage] = useState(initialPage);
  const [total, setTotal] = useState(0);
  const [searchcond, setSearchcond] = useState(initialSearchCond);

  useEffect(() => {
    if (appIsReady && user) {
      fetchMachines(page, searchcond);  // 검색 조건과 페이지를 함께 전달
    }
  }, [page, user, appIsReady]);

  const fetchMachines = async (pageNum, search) => {
    setLoading(true);
    try {
      const response = await apiGetMachines(pageNum, 10, user.accessToken, search);
      if (response && response.data) {
        setMachines(response.data);
        setTotal(response.total);
      } else {
        console.log('No data received');
      }
    } catch (error) {
      console.error('Failed to load machines:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (pageNum) => {
    setPage(pageNum);
    const newSearchParams = new URLSearchParams(location.search);
    newSearchParams.set('page', pageNum);
    newSearchParams.set('searchcond', searchcond);
    navigate({ search: newSearchParams.toString() });
    fetchMachines(pageNum, searchcond);
  };

  const handleSearchChange = (event) => {
    setSearchcond(event.target.value);
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setPage(1);
    const newSearchParams = new URLSearchParams(location.search);
    newSearchParams.set('page', 1);
    newSearchParams.set('searchcond', searchcond);
    navigate({ search: newSearchParams.toString() });
    fetchMachines(1, searchcond);
  };

  const handleEditClick = (machine) => {
    navigate('/machine/edit', { state: { machinedata: machine, page, searchcond } });
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
    return new Date(dateString).toLocaleString('ko-KR', options).replace(/. /g, '-').replace('.', '').replace(' ', ' ').slice(0, 10);
  };

  /**
   * 기기 삭제. 🔴 기기에는 적립 이력이 딸려 있고, 현장에 실물이 있다.
   *    서버는 softDelete 라 `deletedAt` 을 비우면 되살아난다.
   *    ⚠️ 기기를 지워도 **현장 단말은 그대로 동작한다** — 등록만 사라진다.
   */
  const handleDeleteClick = async (machine) => {
    const label = `기기 '${machine.device_name || machine.device_no || machine.device_uuid}'`;
    if (!confirmHardDelete(label)) return;
    try {
      await apiDeleteMachine(machine.id, user.accessToken);
      fetchMachines(page, searchcond);
    } catch (e) {
      alert('삭제에 실패했습니다. 잠시 후 다시 시도해 주세요.');
    }
  };

  const rows = machines.map(machine => ({
    id: machine.id,
    device_no: machine.device_no,
    device_uuid: machine.device_uuid,
    device_name: machine.device_name,
    branch_id: machine.branch_id,
    m_owner_userid: machine.m_owner_userid,
    setup_date: formatDate(machine.setup_date),
    address: machine.address,
    memo: machine.memo,
    information: machine.information,
    last_signal_date: formatDate(machine.last_signal_date),
    last_empty_date: formatDate(machine.last_empty_date),
    status: machine.status === 'on' ? 
      <MDBox ml={-1}>
        <MDBadge badgeContent="online" color="success" variant="gradient" size="sm" />
      </MDBox> : machine.status === 'off' ? 
      <MDBox ml={-1}>
        <MDBadge badgeContent="offline" color="dark" variant="gradient" size="sm" />
      </MDBox> : 
      <MDBox ml={-1}>
        <MDBadge badgeContent="full" color="warning" variant="gradient" size="sm" />
      </MDBox>,
    use_time: machine.use_time,
    edit: 
      <Button 
        variant="contained" 
        color="success" 
        size="small" 
        onClick={() => handleEditClick(machine)}
      >
        수정
      </Button>,
    remove:
      <Button
        variant="outlined"
        color="error"
        size="small"
        onClick={() => handleDeleteClick(machine)}
      >
        삭제
      </Button>
  }));

  const columns = [
    { Header: "ID", accessor: "id", align: "left" },
    { Header: "제품 번호", accessor: "device_no", align: "left" },
    { Header: "UUID", accessor: "device_uuid", align: "left" },
    { Header: "기기 이름", accessor: "device_name", align: "left" },
    { Header: "지점 ID", accessor: "branch_id", align: "left" },
    { Header: "소유자 ID", accessor: "m_owner_userid", align: "left" },
    { Header: "설치 날짜", accessor: "setup_date", align: "left" },
    { Header: "주소", accessor: "address", align: "left" },
    { Header: "메모", accessor: "memo", align: "left" },
    { Header: "마지막 비움 날짜", accessor: "last_empty_date", align: "left" },
    { Header: "상태", accessor: "status", align: "left" },
    { Header: "사용 시간", accessor: "use_time", align: "left" },
    { Header: "수정", accessor: "edit", align: "center" },
    { Header: "삭제", accessor: "remove", align: "center" },
  ];

  const totalPages = Math.ceil(total / 10);
  const maxPageNumbers = 10;
  const currentPageGroup = Math.floor((page - 1) / maxPageNumbers);
  const startPage = currentPageGroup * maxPageNumbers + 1;
  const endPage = Math.min(startPage + maxPageNumbers - 1, totalPages);
  const pageNumbers = Array.from({ length: endPage - startPage + 1 }, (_, index) => startPage + index);


  const handleDownloadExcel = async () => {
    try {
    
      const currentSearchCond = searchcond || "";
        // 엑셀 파일 데이터 가져오기
      const response = await apiExcelMachine(user.accessToken, currentSearchCond);
      console.log(response);
         // JSON 응답을 텍스트로 읽기
    const jsonText = await response.text();
    const jsonData = JSON.parse(jsonText);
    
    // 버퍼 데이터 추출
    const bufferData = jsonData.data.data;
    
    // Uint8Array로 변환
    const uint8Array = new Uint8Array(bufferData);
    
    // 올바른 MIME 타입으로 Blob 생성
    const excelBlob = new Blob([uint8Array], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
    
    // 다운로드 링크 생성
    const url = window.URL.createObjectURL(excelBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = '기기관리.xlsx';
    document.body.appendChild(a);
    a.click();
    
    // 정리
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
      } catch (error) {
        console.error('다운로드 실패:', error);
      }
};


  return (
    <DashboardLayout>
      <TopNavbar 
        searchlabel="제품번호,UUID,기기이름,소유자id"
        onSearchChange={handleSearchChange}
        onSearchSubmit={handleSearchSubmit}
      />
      <MDBox display="flex" mr={2} justifyContent="right">
        <MDButton variant="gradient" color="info" onClick={handleDownloadExcel} sx={{ ml: 2 }}>엑셀 다운로드</MDButton>
      </MDBox>
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  기기관리
                </MDTypography>
              </MDBox>
              <MDBox pt={3}>
                <DataTable
                  table={{ columns, rows }}
                  isSorted={false}
                  entriesPerPage={false}
                  showTotalEntries={false}
                  noEndBorder
                  loading={loading}
                />
              </MDBox>
              <MDBox
                display="flex"
                flexDirection={{ xs: "column", sm: "row" }}
                justifyContent="center"
                alignItems={{ xs: "flex-start", sm: "center" }}
                p={3}
              >
                <MDPagination
                  variant="gradient"
                  color="info"
                >
                  {page > 1 && (
                    <MDPagination item onClick={() => handlePageChange(page - 1)}>
                      <Icon sx={{ fontWeight: "bold" }}>chevron_left</Icon>
                    </MDPagination>
                  )}
                  {pageNumbers.map(pageNum => (
                    <MDPagination
                      item
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      active={page === pageNum}
                    >
                      {pageNum}
                    </MDPagination>
                  ))}
                  {page < totalPages && (
                    <MDPagination item onClick={() => handlePageChange(page + 1)}>
                      <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
                    </MDPagination>
                  )}
                </MDPagination>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Machines;
